import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { seoApi } from '../../utils/api';
import {
    Search,
    Globe,
    TrendingUp,
    FileText,
    Loader2,
    Plus,
    RefreshCw,
    AlertTriangle,
    CheckCircle,
    XCircle,
    Sparkles,
    Target,
    BarChart3,
    ExternalLink
} from 'lucide-react';

export default function SEOModule() {
    const { projectId } = useParams();
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState<'audits' | 'keywords' | 'content'>('audits');
    const [seedKeywords, setSeedKeywords] = useState('');
    const [contentTopic, setContentTopic] = useState('');
    const [contentType, setContentType] = useState('blog');

    // Fetch audits
    const { data: auditsData, isLoading: auditsLoading } = useQuery({
        queryKey: ['seo-audits', projectId],
        queryFn: () => seoApi.listAudits(projectId!),
        enabled: !!projectId
    });

    // Fetch keywords
    const { data: keywordsData, isLoading: keywordsLoading } = useQuery({
        queryKey: ['seo-keywords', projectId],
        queryFn: () => seoApi.getKeywords(projectId!),
        enabled: !!projectId
    });

    // Fetch content
    const { data: contentData, isLoading: contentLoading } = useQuery({
        queryKey: ['seo-content', projectId],
        queryFn: () => seoApi.getContent(projectId!),
        enabled: !!projectId
    });

    // Run audit mutation
    const runAuditMutation = useMutation({
        mutationFn: () => seoApi.audit(projectId!),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['seo-audits', projectId] });
        }
    });

    // Research keywords mutation
    const researchKeywordsMutation = useMutation({
        mutationFn: (keywords: string[]) => seoApi.researchKeywords(projectId!, keywords),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['seo-keywords', projectId] });
            setSeedKeywords('');
        }
    });

    // Generate content mutation
    const generateContentMutation = useMutation({
        mutationFn: (data: any) => seoApi.generateContent(projectId!, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['seo-content', projectId] });
            setContentTopic('');
        }
    });

    const audits = auditsData?.data?.audits || [];
    const keywords = keywordsData?.data?.keywords || [];
    const contents = contentData?.data?.content || [];

    const getScoreColor = (score: number) => {
        if (score >= 80) return 'text-green-400';
        if (score >= 60) return 'text-yellow-400';
        return 'text-red-400';
    };

    const getScoreBg = (score: number) => {
        if (score >= 80) return 'bg-green-500/20';
        if (score >= 60) return 'bg-yellow-500/20';
        return 'bg-red-500/20';
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold flex items-center gap-3">
                        <Search className="w-8 h-8 text-primary-400" />
                        SEO Module
                    </h1>
                    <p className="text-dark-400 mt-1">Audit your site, research keywords, and generate optimized content</p>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b border-dark-800 pb-2">
                {[
                    { id: 'audits', label: 'Site Audits', icon: Globe },
                    { id: 'keywords', label: 'Keywords', icon: Target },
                    { id: 'content', label: 'Content', icon: FileText }
                ].map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id as any)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${activeTab === tab.id
                                ? 'bg-primary-600/20 text-primary-400'
                                : 'text-dark-400 hover:text-white hover:bg-dark-800'
                            }`}
                    >
                        <tab.icon className="w-4 h-4" />
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Audits Tab */}
            {activeTab === 'audits' && (
                <div className="space-y-6">
                    {/* Run Audit Card */}
                    <div className="card p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-xl font-semibold">Site Audit</h2>
                                <p className="text-dark-400 text-sm">Analyze your website for SEO issues and opportunities</p>
                            </div>
                            <button
                                onClick={() => runAuditMutation.mutate()}
                                disabled={runAuditMutation.isPending}
                                className="btn btn-primary flex items-center gap-2"
                            >
                                {runAuditMutation.isPending ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <RefreshCw className="w-4 h-4" />
                                )}
                                Run New Audit
                            </button>
                        </div>
                    </div>

                    {/* Audits List */}
                    {auditsLoading ? (
                        <div className="flex justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
                        </div>
                    ) : audits.length === 0 ? (
                        <div className="card p-12 text-center">
                            <Globe className="w-12 h-12 text-dark-600 mx-auto mb-4" />
                            <h3 className="text-xl font-semibold mb-2">No audits yet</h3>
                            <p className="text-dark-400 mb-4">Run your first SEO audit to identify optimization opportunities</p>
                        </div>
                    ) : (
                        <div className="grid gap-4">
                            {audits.map((audit: any) => (
                                <div key={audit.id} className="card p-6">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-4">
                                            <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${getScoreBg(audit.overallScore || 0)}`}>
                                                <span className={`text-2xl font-bold ${getScoreColor(audit.overallScore || 0)}`}>
                                                    {audit.overallScore || '--'}
                                                </span>
                                            </div>
                                            <div>
                                                <h3 className="font-semibold">{audit.url}</h3>
                                                <p className="text-dark-400 text-sm">
                                                    {new Date(audit.createdAt).toLocaleDateString()} •
                                                    Status: <span className={audit.status === 'completed' ? 'text-green-400' : 'text-yellow-400'}>
                                                        {audit.status}
                                                    </span>
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            {audit.issues && (
                                                <div className="flex items-center gap-4 text-sm">
                                                    <span className="flex items-center gap-1 text-red-400">
                                                        <XCircle className="w-4 h-4" />
                                                        {audit.issues.critical || 0} Critical
                                                    </span>
                                                    <span className="flex items-center gap-1 text-yellow-400">
                                                        <AlertTriangle className="w-4 h-4" />
                                                        {audit.issues.warnings || 0} Warnings
                                                    </span>
                                                    <span className="flex items-center gap-1 text-green-400">
                                                        <CheckCircle className="w-4 h-4" />
                                                        {audit.issues.passed || 0} Passed
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Keywords Tab */}
            {activeTab === 'keywords' && (
                <div className="space-y-6">
                    {/* Research Keywords Card */}
                    <div className="card p-6">
                        <h2 className="text-xl font-semibold mb-4">AI Keyword Research</h2>
                        <div className="flex gap-4">
                            <input
                                type="text"
                                value={seedKeywords}
                                onChange={(e) => setSeedKeywords(e.target.value)}
                                className="input flex-1"
                                placeholder="Enter seed keywords (comma separated)..."
                            />
                            <button
                                onClick={() => researchKeywordsMutation.mutate(seedKeywords.split(',').map(k => k.trim()))}
                                disabled={!seedKeywords || researchKeywordsMutation.isPending}
                                className="btn btn-primary flex items-center gap-2"
                            >
                                {researchKeywordsMutation.isPending ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Sparkles className="w-4 h-4" />
                                )}
                                Research Keywords
                            </button>
                        </div>
                    </div>

                    {/* Keywords Table */}
                    {keywordsLoading ? (
                        <div className="flex justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
                        </div>
                    ) : keywords.length === 0 ? (
                        <div className="card p-12 text-center">
                            <Target className="w-12 h-12 text-dark-600 mx-auto mb-4" />
                            <h3 className="text-xl font-semibold mb-2">No keywords yet</h3>
                            <p className="text-dark-400">Enter seed keywords above to discover opportunities</p>
                        </div>
                    ) : (
                        <div className="card overflow-hidden">
                            <table className="w-full">
                                <thead className="bg-dark-800/50">
                                    <tr>
                                        <th className="text-left p-4 font-semibold">Keyword</th>
                                        <th className="text-left p-4 font-semibold">Intent</th>
                                        <th className="text-left p-4 font-semibold">Difficulty</th>
                                        <th className="text-left p-4 font-semibold">Volume</th>
                                        <th className="text-left p-4 font-semibold">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {keywords.map((kw: any) => (
                                        <tr key={kw.id} className="border-t border-dark-800 hover:bg-dark-800/30">
                                            <td className="p-4 font-medium">{kw.keyword}</td>
                                            <td className="p-4">
                                                <span className="px-2 py-1 rounded-full text-xs bg-primary-600/20 text-primary-400">
                                                    {kw.intent || 'informational'}
                                                </span>
                                            </td>
                                            <td className="p-4">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-20 h-2 bg-dark-700 rounded-full overflow-hidden">
                                                        <div
                                                            className={`h-full ${kw.difficulty > 70 ? 'bg-red-500' : kw.difficulty > 40 ? 'bg-yellow-500' : 'bg-green-500'}`}
                                                            style={{ width: `${kw.difficulty || 50}%` }}
                                                        />
                                                    </div>
                                                    <span className="text-sm text-dark-400">{kw.difficulty || 50}</span>
                                                </div>
                                            </td>
                                            <td className="p-4 text-dark-300">{kw.searchVolume?.toLocaleString() || '--'}</td>
                                            <td className="p-4">
                                                <span className={`px-2 py-1 rounded-full text-xs ${kw.status === 'tracked' ? 'bg-green-500/20 text-green-400' : 'bg-dark-700 text-dark-400'
                                                    }`}>
                                                    {kw.status || 'discovered'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Content Tab */}
            {activeTab === 'content' && (
                <div className="space-y-6">
                    {/* Generate Content Card */}
                    <div className="card p-6">
                        <h2 className="text-xl font-semibold mb-4">AI Content Generator</h2>
                        <div className="grid gap-4 md:grid-cols-3">
                            <div className="md:col-span-2">
                                <label className="label">Topic / Title</label>
                                <input
                                    type="text"
                                    value={contentTopic}
                                    onChange={(e) => setContentTopic(e.target.value)}
                                    className="input"
                                    placeholder="Enter content topic..."
                                />
                            </div>
                            <div>
                                <label className="label">Content Type</label>
                                <select
                                    value={contentType}
                                    onChange={(e) => setContentType(e.target.value)}
                                    className="input"
                                >
                                    <option value="blog">Blog Post</option>
                                    <option value="landing">Landing Page</option>
                                    <option value="product">Product Description</option>
                                    <option value="meta">Meta Description</option>
                                </select>
                            </div>
                        </div>
                        <button
                            onClick={() => generateContentMutation.mutate({ topic: contentTopic, type: contentType })}
                            disabled={!contentTopic || generateContentMutation.isPending}
                            className="btn btn-primary mt-4 flex items-center gap-2"
                        >
                            {generateContentMutation.isPending ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Sparkles className="w-4 h-4" />
                            )}
                            Generate Content
                        </button>
                    </div>

                    {/* Content List */}
                    {contentLoading ? (
                        <div className="flex justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
                        </div>
                    ) : contents.length === 0 ? (
                        <div className="card p-12 text-center">
                            <FileText className="w-12 h-12 text-dark-600 mx-auto mb-4" />
                            <h3 className="text-xl font-semibold mb-2">No content generated yet</h3>
                            <p className="text-dark-400">Use AI to generate SEO-optimized content for your site</p>
                        </div>
                    ) : (
                        <div className="grid gap-4">
                            {contents.map((content: any) => (
                                <div key={content.id} className="card p-6">
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <span className="text-xs px-2 py-1 rounded-full bg-primary-600/20 text-primary-400 mb-2 inline-block">
                                                {content.type}
                                            </span>
                                            <h3 className="font-semibold text-lg">{content.title}</h3>
                                            <p className="text-dark-400 text-sm mt-1 line-clamp-2">
                                                {content.content?.substring(0, 200)}...
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {content.seoScore && (
                                                <div className={`px-3 py-1 rounded-full text-sm ${getScoreBg(content.seoScore)} ${getScoreColor(content.seoScore)}`}>
                                                    SEO: {content.seoScore}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4 mt-4 pt-4 border-t border-dark-800 text-sm text-dark-400">
                                        <span>{new Date(content.createdAt).toLocaleDateString()}</span>
                                        {content.wordCount && <span>{content.wordCount} words</span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
